import React, {
  CSSProperties,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import styles from "./styles.module.scss";
import { Minus, Plus } from "lucide-react";
import classNames from "classnames";
import { convertImageUrlToBase64, FormatCanvasResult } from "../../utils";
import UserGuidePopover from "../UserGuidePopover";
import { assetStatic } from "../../mock";
import { Spin } from "antd";
import { initializeCanvasEvents, renderToCanvas } from "./utils";

interface CanvasProps {
  activeImage: { mask: string; image: string };
  activeNavIndex: number;
  draggableEl: { image: string; style: CSSProperties }[];
  onMouseUpDraggableItems: (
    e: React.MouseEvent<HTMLDivElement>,
    i: number,
  ) => void;
  activeBtn: HTMLElement;
  generatedImage: string;
  isLoading: boolean;
  onApplyImg: (img: string) => void;
  onNewRender: () => void;
  setApplyImg: (v: boolean) => void;
  userGuide: boolean;
  setUserGuide: (v: boolean) => void;
  currStep: number;
  setCurrUserStepGuide: (v: number) => void;
  setActiveNavIndex: (v: number) => void;
  onGenerateImage: (p: string[]) => void;
  selectedPrompt?: string;
  setSelectedPrompt: (v: string) => void;
}

function addMimeType(img: string): string {
  if (!img) {
    return "";
  }
  if (img.startsWith("data:image")) {
    return img;
  }

  return `data:image/png;base64,${img}`;
}

function useZoom(isMobile: boolean, userGuide: boolean) {
  const [zoom, setZoom] = useState(isMobile ? 0.55 : 1);
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  const changeZoom = (delta: number) => {
    if (userGuide) return zoomRef.current;
    const min = 0.2,
      max = isMobile ? 0.55 : 1.2;
    const next = Math.min(Math.max(zoomRef.current + delta, min), max);
    zoomRef.current = next;
    setZoom(next);
    return next;
  };
  return { zoom, zoomRef, changeZoom };
}

function useCenterDefaults(container: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    const dom = container.current;
    if (!dom) return;
    dom
      .querySelectorAll<HTMLElement>('[data-default-position="center"]')
      .forEach((el) => {
        const p = el.offsetParent as HTMLElement;
        el.style.top = `${(p.offsetHeight - el.offsetHeight) / 2}px`;
        el.style.left = `${(p.offsetWidth - el.offsetWidth) / 2}px`;
      });
  }, [container]);
}

function useCanvasLifecycle(
  container: React.RefObject<HTMLDivElement>,
  cRef: React.RefObject<HTMLCanvasElement>,
  vRef: React.RefObject<HTMLCanvasElement>,
  deps: any[],
  onNewRender: () => void,
  changeZoom: (d: number) => number,
  zoomRef: React.RefObject<number>,
) {
  useEffect(() => {
    const dom = container.current;
    if (!dom) return;
    const cleanup = initializeCanvasEvents({
      canvasDom: dom,
      canvasRef2: cRef,
      virtualCanvasRef: vRef,
      onNewRender,
      changeZoom,
      zoomRef,
    });
    return cleanup;
  }, [container, onNewRender, ...deps]);
  useEffect(() => {
    if (cRef.current)
      renderToCanvas({ canvasRef: cRef, resultBlockId: "imagesItemWorkspace" });
    if (vRef.current)
      renderToCanvas({ canvasRef: vRef, resultBlockId: "imagesItemWorkspace" });
  }, deps);
}

interface UGProps {
  condition: boolean;
  placement: "right" | "bottom";
  step: number;
  text: number;
  nextStep: number;
  additionalHandlers?: any;
  setCurrUserStepGuide: (n: number) => void;
  setUserGuide: (b: boolean) => void;
  children: ReactNode;
}
const MaybeUserGuide: React.FC<UGProps> = ({
  condition,
  children,
  ...popover
}) =>
  condition ? (
    <UserGuidePopover {...popover}>{children}</UserGuidePopover>
  ) : (
    <>{children}</>
  );

const WorkspaceSection: React.FC<{
  url: string;
  activeNavIndex: number;
  isLoading: boolean;
}> = ({ url, activeNavIndex, isLoading }) => (
  <div className={classNames(styles.imagesItem, styles.imagesItemWorkspace)}>
    <div className={styles.imagesLabel}>
      Workspace · drop your images & assets here
    </div>
    <div
      className={styles.imagesBox}
      data-draggable="container"
      id="imagesItemWorkspace"
    >
      <div
        className={classNames(
          styles.draggable,
          !url && styles.notHover,
          url && activeNavIndex === 0 && !isLoading && styles.active,
        )}
        style={{
          pointerEvents:
            url && activeNavIndex === 0 && !isLoading ? "auto" : "none",
        }}
        data-draggable="draggable"
        data-type="workspace"
        data-default-position="center"
      >
        {url && (
          <div
            data-draggable="image"
            className={styles.draggableImage}
            style={{ backgroundImage: `url("${url}")` }}
          />
        )}
        <DraggableResize />
      </div>
    </div>
  </div>
);

const ResultSection: React.FC<{
  url: string;
  isLoading: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}> = ({ url, isLoading, canvasRef }) => (
  <div className={classNames(styles.imagesItem, styles.imagesItemResult)}>
    <div className={styles.imagesLabel}>Result</div>
    <div className={classNames(styles.imagesBox, isLoading && styles.loading)}>
      {isLoading && <Spin size="large" className={styles.loader} />}
      {url && (
        <div
          style={{
            backgroundImage: `url("${url}")`,
            backgroundSize: "100% 100%",
            position: "absolute",
            inset: 0,
            borderRadius: "8px",
          }}
        />
      )}
      <canvas
        ref={canvasRef}
        width={338}
        height={338}
        style={{ position: "absolute", inset: 0 }}
      />
    </div>
  </div>
);

const ControlsBar: React.FC<{
  activeImage: boolean;
  onReset: () => void;
  onGenerate: () => void;
  disabled: boolean;
}> = ({ activeImage, onReset, onGenerate, disabled }) =>
  activeImage ? (
    <div className={styles.control}>
      <button
        className={classNames("button button--m-0", styles.reset)}
        onClick={onReset}
      >
        Reset
      </button>
      <button
        className={classNames(
          "button button--m-0",
          styles.generate,
          disabled && styles.disabled,
        )}
        onClick={onGenerate}
        disabled={disabled}
      >
        Generate image
      </button>
    </div>
  ) : null;

const Canvas: React.FC<CanvasProps> = ({
  activeImage,
  activeNavIndex,
  draggableEl,
  onMouseUpDraggableItems,
  activeBtn,
  generatedImage,
  isLoading,
  onApplyImg,
  onNewRender,
  setApplyImg,
  userGuide,
  setUserGuide,
  currStep,
  setCurrUserStepGuide,
  setActiveNavIndex,
  onGenerateImage,
  selectedPrompt,
  setSelectedPrompt,
}) => {
  const isMobile = window.innerWidth < 768;
  const { zoom, zoomRef, changeZoom } = useZoom(isMobile, userGuide);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const virtualRef = useRef<HTMLCanvasElement>(null);

  useCenterDefaults(containerRef);
  useCanvasLifecycle(
    containerRef,
    canvasRef,
    virtualRef,
    [activeImage, draggableEl, isLoading, userGuide],
    onNewRender,
    changeZoom,
    zoomRef,
  );

  const [genBase64, setGenBase64] = useState("");
  useEffect(() => {
    (async () => {
      if (generatedImage && !generatedImage.startsWith("data:image"))
        setGenBase64(await convertImageUrlToBase64(generatedImage));
      else setGenBase64(generatedImage);
    })();
  }, [generatedImage]);

  const applyCanvas = () => {
    if (!canvasRef.current || !virtualRef.current) return;
    renderToCanvas({ canvasRef, resultBlockId: "imagesItemWorkspace" }).then(
      () =>
        renderToCanvas({
          canvasRef: virtualRef,
          resultBlockId: "imagesItemWorkspace",
        }).then(() => onApplyImg(FormatCanvasResult(virtualRef))),
    );
  };
  useEffect(applyCanvas, [activeImage, draggableEl]);

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>, idx: number) => {
    onMouseUpDraggableItems(e, idx);
    applyCanvas();
  };

  const imgSrc = activeImage?.image || "";
  const wsUrl = userGuide ? imgSrc : addMimeType(imgSrc);
  const resUrl = userGuide ? genBase64 : addMimeType(genBase64);

  return (
    <div
      ref={containerRef}
      className={styles.canvas}
      data-draggable="container"
    >
      <Zoom
        zoom={zoom}
        onZoomIn={() => changeZoom(0.05)}
        onZoomOut={() => changeZoom(-0.05)}
      />
      <div
        className={styles.canvasInner}
        style={{
          transform: `scale3D(${zoom},${zoom},1)`,
          transition: "transform 0.1s",
        }}
      >
        <div className={styles.images}>
          <MaybeUserGuide
            condition={userGuide && (currStep === 2 || currStep === 7)}
            placement="right"
            step={currStep === 2 ? 0 : 2}
            text={1}
            nextStep={currStep === 2 ? 3 : 8}
            setCurrUserStepGuide={setCurrUserStepGuide}
            additionalHandlers={
              currStep !== 7 ? { setActiveNavIndex } : { setUserGuide }
            }
            setUserGuide={setUserGuide}
          >
            <WorkspaceSection
              url={wsUrl}
              activeNavIndex={activeNavIndex}
              isLoading={isLoading}
            />
          </MaybeUserGuide>
          <div className="zIndexCopy">
            <DraggableItems
              items={
                userGuide && currStep === 7
                  ? [
                      {
                        image: assetStatic,
                        style: {
                          top: "35%",
                          left: "180px",
                          width: "150px",
                          height: "150px",
                        },
                      },
                    ]
                  : draggableEl
              }
              activeBtn={activeBtn}
              userGuide={userGuide}
              onPointerUp={handleMouseUp}
            />
          </div>
          <MaybeUserGuide
            condition={userGuide && currStep === 5}
            placement="bottom"
            step={1}
            text={3}
            nextStep={6}
            setCurrUserStepGuide={setCurrUserStepGuide}
            additionalHandlers={{ setActiveNavIndex }}
            setUserGuide={setUserGuide}
          >
            <ResultSection
              url={resUrl}
              isLoading={isLoading}
              canvasRef={canvasRef}
            />
          </MaybeUserGuide>
        </div>
        <canvas
          ref={virtualRef}
          width={1024}
          height={1024}
          style={{ display: "none" }}
        />
        <ControlsBar
          activeImage={!!imgSrc}
          onReset={() => {
            setApplyImg(false);
            setSelectedPrompt("");
            onNewRender();
          }}
          onGenerate={() => onGenerateImage([selectedPrompt || ""])}
          disabled={!selectedPrompt}
        />
      </div>
    </div>
  );
};

export default Canvas;

const Zoom: React.FC<{
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
}> = ({ zoom, onZoomIn, onZoomOut }) => (
  <div className={styles.zoom}>
    <button className={styles.zoomButton} onClick={onZoomOut}>
      <Minus size={12} />
    </button>
    <span>{Math.round(zoom * 100)}%</span>
    <button className={styles.zoomButton} onClick={onZoomIn}>
      <Plus size={12} />
    </button>
  </div>
);

const DraggableItems: React.FC<{
  items: { image: string; style: CSSProperties }[];
  onPointerUp: (e: React.MouseEvent<HTMLDivElement>, i: number) => void;
  activeBtn: HTMLElement;
  userGuide?: boolean;
}> = ({ items, onPointerUp, activeBtn, userGuide }) => (
  <>
    {items.map((el, idx) => {
      const isActive = idx === items.length - 1 && activeBtn;
      return (
        <div
          key={el.image}
          className={classNames(styles.draggable, isActive && styles.active)}
          style={el.style}
          data-draggable="draggable"
          onPointerUp={(e) => onPointerUp(e, idx)}
        >
          <div
            data-draggable="image"
            className={styles.draggableImage}
            style={{
              backgroundImage: userGuide
                ? `url("${el.image}")`
                : `url('data:image/png;base64,${el.image}')`,
            }}
          />
          <DraggableResize showButton />
        </div>
      );
    })}
  </>
);

const cornerOffsets = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
] as const;

export const DraggableResize: React.FC<{ showButton?: boolean }> = ({
  showButton,
}) => {
  return (
    <div className={classNames(styles.resize)}>
      {cornerOffsets.map(([dx, dy]) => (
        <div
          key={`${dx}-${dy}`}
          className={styles.resizeDot}
          data-draggable="resize"
          data-x={dx}
          data-y={dy}
        />
      ))}

      {showButton && (
        <div className={styles.resizeButtons}>
          <button
            className={styles.resizeButton}
            data-draggable="button"
            data-position="front"
          >
            Move to front
          </button>
          <button
            className={styles.resizeButton}
            data-draggable="button"
            data-position="back"
          >
            Move to back
          </button>
        </div>
      )}
    </div>
  );
};
